import React, { useState } from 'react';
import { PageShell, DataTable } from '../../components/Ui';
import { useHmsRecords } from '../../hooks/useHmsRecords';

/* ═══════════════════════════════════════════════════════════
   INFECTION CONTROL / CSSD MODULE (NABH HIC compliant)
   - HAI Surveillance & Reporting
   - Antibiotic Stewardship Program
   - CSSD Sterilization Tracking
   - Hand Hygiene Compliance
   - Bundle Compliance (VAP, CLABSI, CAUTI, SSI)
   - Outbreak Alerts
   - Monthly HIC Report
═══════════════════════════════════════════════════════════ */

const HAI_TYPES = ['VAP (Ventilator-Associated Pneumonia)', 'CLABSI (Central Line BSI)', 'CAUTI (Catheter-UTI)', 'SSI (Surgical Site Infection)', 'CDiff (C. difficile)', 'MRSA Bacteremia', 'VRE', 'CRBSI'];
const WARDS = ['ICU', 'CCU', 'NICU', 'Medical Ward', 'Surgical Ward', 'Gynae Ward', 'Pediatric Ward', 'Burns Unit', 'OT Complex'];
const ORGANISMS = ['E. coli', 'Klebsiella pneumoniae', 'Staphylococcus aureus (MRSA)', 'Pseudomonas aeruginosa', 'Acinetobacter baumannii', 'Enterococcus (VRE)', 'C. difficile', 'Candida species', 'Streptococcus', 'Proteus mirabilis'];
const ANTIBIOTICS = ['Meropenem', 'Piperacillin-Tazobactam', 'Vancomycin', 'Linezolid', 'Colistin', 'Cefoperazone-Sulbactam', 'Amikacin', 'Tigecycline', 'Daptomycin', 'Ceftazidime-Avibactam'];
const STERIL_METHODS = ['Autoclave (121°C)', 'Autoclave (134°C)', 'ETO Gas Sterilization', 'Plasma Sterilization (H₂O₂)', 'Dry Heat Oven', 'Chemical (Glutaraldehyde)', 'UV Sterilization'];

function HaiSurveillanceTab() {
  const { records: hais, create } = useHmsRecords('infection-hai');
  const [form, setForm] = useState({ type: 'VAP (Ventilator-Associated Pneumonia)', patient: '', uhid: '', ward: 'ICU', organism: '', onsetDate: new Date().toISOString().slice(0, 10), deviceDays: 0, reportedBy: '', severity: 'Medium' });
  const [showForm, setShowForm] = useState(false);

  const submit = async (e) => { e.preventDefault(); await create({ ...form, deviceDays: Number(form.deviceDays || 0), status: 'Active' }); setShowForm(false); };
  const sColor = { High: '#dc2626', Medium: '#f59e0b', Low: '#10b981' };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Active HAIs', val: hais.filter(h => h.status === 'Active').length, color: '#dc2626' }, { label: 'This Month', val: hais.length, color: '#f97316' }, { label: 'VAP Cases', val: hais.filter(h => h.type.includes('VAP')).length, color: '#8b5cf6' }, { label: 'MRSA Cases', val: hais.filter(h => h.organism?.includes('MRSA')).length, color: '#ef4444' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 12 }}><button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Report New HAI'}</button></div>
      {showForm && (
        <form className="card mb form-grid" style={{ borderLeft: '4px solid #dc2626' }} onSubmit={submit}>
          <h3 className="full" style={{ marginTop: 0, color: '#dc2626' }}>🦠 New HAI Report</h3>
          <div className="field"><label>HAI Type *</label><select className="input" required value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>{HAI_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
          <div className="field"><label>Patient Name *</label><input className="input" required value={form.patient} onChange={e => setForm(f => ({ ...f, patient: e.target.value }))} /></div>
          <div className="field"><label>UHID</label><input className="input" value={form.uhid} onChange={e => setForm(f => ({ ...f, uhid: e.target.value }))} /></div>
          <div className="field"><label>Ward</label><select className="input" value={form.ward} onChange={e => setForm(f => ({ ...f, ward: e.target.value }))}>{WARDS.map(w => <option key={w}>{w}</option>)}</select></div>
          <div className="field"><label>Causative Organism</label><select className="input" value={form.organism} onChange={e => setForm(f => ({ ...f, organism: e.target.value }))}><option value="">Unknown / Pending</option>{ORGANISMS.map(o => <option key={o}>{o}</option>)}</select></div>
          <div className="field"><label>Onset Date</label><input type="date" className="input" value={form.onsetDate} onChange={e => setForm(f => ({ ...f, onsetDate: e.target.value }))} /></div>
          <div className="field"><label>Device Days</label><input type="number" className="input" value={form.deviceDays} onChange={e => setForm(f => ({ ...f, deviceDays: e.target.value }))} /></div>
          <div className="field"><label>Severity</label><select className="input" value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}><option>High</option><option>Medium</option><option>Low</option></select></div>
          <div className="field"><label>Reported By</label><input className="input" value={form.reportedBy} onChange={e => setForm(f => ({ ...f, reportedBy: e.target.value }))} /></div>
          <div className="full form-actions"><button type="submit" className="btn btn-primary">🦠 Submit HAI Report</button></div>
        </form>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>HAI Register</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>ID</th><th>HAI Type</th><th>Patient</th><th>Ward</th><th>Organism</th><th>Onset</th><th>Severity</th><th>Status</th></tr></thead>
            <tbody>
              {hais.map(h => (
                <tr key={h.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#dc2626', fontWeight: 700 }}>{h.id}</td>
                  <td style={{ fontSize: 12, fontWeight: 600 }}>{h.type}</td>
                  <td><div style={{ fontWeight: 700 }}>{h.patient}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{h.uhid}</div></td>
                  <td>{h.ward}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{h.organism || '—'}</td>
                  <td>{h.onsetDate}</td>
                  <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: sColor[h.severity] + '18', color: sColor[h.severity], border: `1px solid ${sColor[h.severity]}40` }}>{h.severity}</span></td>
                  <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: h.status === 'Active' ? '#fef2f2' : '#f0fdf4', color: h.status === 'Active' ? '#dc2626' : '#16a34a', border: `1px solid ${h.status === 'Active' ? '#fca5a5' : '#86efac'}` }}>{h.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AntibioticTab() {
  const { records: requests, create, loading } = useHmsRecords('infection-antibiotic');
  const [form, setForm] = useState({ patient: '', antibiotic: ANTIBIOTICS[0], dose: '', duration: '', indication: '', culture: '' });
  const [showForm, setShowForm] = useState(false);

  const submit = async () => {
    if (!form.patient) return;
    await create({ ...form, status: 'Pending ID Approval', approvedBy: '', startDate: new Date().toISOString().slice(0, 10) });
    setForm({ patient: '', antibiotic: ANTIBIOTICS[0], dose: '', duration: '', indication: '', culture: '' });
    setShowForm(false);
  };

  return (
    <div>
      <div style={{ padding: '12px 16px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 10, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, color: '#c2410c', marginBottom: 4 }}>⚠ Antibiotic Stewardship Program (ASP)</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>Restricted antibiotics require ID Consultant or Microbiologist approval. All high-alert antibiotics are monitored for de-escalation and outcome.</div>
      </div>
      <div style={{ marginBottom: 12 }}><button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ New Restricted Antibiotic Request'}</button></div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #f97316' }}>
          <div className="field"><label>Patient Name</label><input className="input" value={form.patient} onChange={e => setForm(f => ({ ...f, patient: e.target.value }))} /></div>
          <div className="field"><label>Antibiotic</label><select className="input" value={form.antibiotic} onChange={e => setForm(f => ({ ...f, antibiotic: e.target.value }))}>{ANTIBIOTICS.map(a => <option key={a}>{a}</option>)}</select></div>
          <div className="field"><label>Dose & Frequency</label><input className="input" value={form.dose} onChange={e => setForm(f => ({ ...f, dose: e.target.value }))} placeholder="e.g. 1g IV TDS" /></div>
          <div className="field"><label>Duration</label><input className="input" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} placeholder="e.g. 7 days" /></div>
          <div className="field full"><label>Clinical Indication</label><textarea className="input" rows={2} value={form.indication} onChange={e => setForm(f => ({ ...f, indication: e.target.value }))} /></div>
          <div className="field full"><label>Culture / Sensitivity Results</label><textarea className="input" rows={2} value={form.culture} onChange={e => setForm(f => ({ ...f, culture: e.target.value }))} /></div>
          <div className="full form-actions"><button type="button" className="btn btn-primary" onClick={submit}>Submit Request</button></div>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Restricted Antibiotic Requests</h3>
        {loading && <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>Loading...</div>}
        {!loading && requests.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>No antibiotic requests recorded yet. Submit the first request above.</div>}
        {requests.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>ID</th><th>Patient</th><th>Antibiotic</th><th>Dose</th><th>Indication</th><th>Approved By</th><th>Status</th></tr></thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#f97316', fontWeight: 700 }}>{r.id}</td>
                    <td style={{ fontWeight: 700 }}>{r.patient}</td>
                    <td style={{ fontWeight: 700, color: '#dc2626' }}>{r.antibiotic}</td>
                    <td style={{ fontSize: 12 }}>{r.dose} · {r.duration}</td>
                    <td style={{ fontSize: 12, color: '#64748b', maxWidth: 200 }}>{r.indication}</td>
                    <td style={{ fontSize: 12 }}>{r.approvedBy || '—'}</td>
                    <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: r.status === 'Approved' ? '#f0fdf4' : '#fff7ed', color: r.status === 'Approved' ? '#16a34a' : '#c2410c', border: `1px solid ${r.status === 'Approved' ? '#86efac' : '#fed7aa'}` }}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CssdTab() {
  const { records: loads, create, loading } = useHmsRecords('infection-cssd');
  const [form, setForm] = useState({ items: '', method: STERIL_METHODS[0], cycleNo: '', startTime: '', endTime: '', temp: '', pressure: '', completedBy: '', ward: WARDS[0] });
  const [showForm, setShowForm] = useState(false);

  const submit = async () => {
    if (!form.items) return;
    await create({ ...form, temp: Number(form.temp || 0), pressure: form.pressure ? Number(form.pressure) : null, bi: 'Pending', ci: 'Pass', status: 'In Process', date: new Date().toISOString().slice(0, 10) });
    setForm({ items: '', method: STERIL_METHODS[0], cycleNo: '', startTime: '', endTime: '', temp: '', pressure: '', completedBy: '', ward: WARDS[0] });
    setShowForm(false);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Total Loads', val: loads.length, color: '#10b981' }, { label: 'Released', val: loads.filter(l => l.status === 'Released').length, color: '#10b981' }, { label: 'In Process', val: loads.filter(l => l.status === 'In Process').length, color: '#f59e0b' }, { label: 'BI Fails', val: loads.filter(l => l.bi === 'Fail').length, color: '#dc2626' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 12 }}><button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Log Sterilization Cycle'}</button></div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #10b981' }}>
          <div className="field full"><label>Items Sterilized *</label><input className="input" required value={form.items} onChange={e => setForm(f => ({ ...f, items: e.target.value }))} placeholder="e.g. General Surgery Set, 3 trays" /></div>
          <div className="field"><label>Method *</label><select className="input" value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}>{STERIL_METHODS.map(m => <option key={m}>{m}</option>)}</select></div>
          <div className="field"><label>Cycle No.</label><input className="input" value={form.cycleNo} onChange={e => setForm(f => ({ ...f, cycleNo: e.target.value }))} placeholder="AC-YYYY-XXX" /></div>
          <div className="field"><label>Start Time</label><input type="time" className="input" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} /></div>
          <div className="field"><label>End Time</label><input type="time" className="input" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} /></div>
          <div className="field"><label>Temp (°C)</label><input type="number" className="input" value={form.temp} onChange={e => setForm(f => ({ ...f, temp: e.target.value }))} /></div>
          <div className="field"><label>Ward / Department</label><select className="input" value={form.ward} onChange={e => setForm(f => ({ ...f, ward: e.target.value }))}>{WARDS.map(w => <option key={w}>{w}</option>)}</select></div>
          <div className="field"><label>Completed By</label><input className="input" value={form.completedBy} onChange={e => setForm(f => ({ ...f, completedBy: e.target.value }))} /></div>
          <div className="full form-actions"><button type="button" className="btn btn-primary" onClick={submit}>Log Cycle</button></div>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>CSSD Sterilization Register</h3>
        {loading && <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>Loading...</div>}
        {!loading && loads.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>No sterilization cycles recorded yet. Log the first cycle above.</div>}
        {loads.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Cycle</th><th>Items</th><th>Method</th><th>Time</th><th>Temp</th><th>BI Test</th><th>CI Test</th><th>Completed By</th><th>Status</th></tr></thead>
              <tbody>
                {loads.map(l => (
                  <tr key={l.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#10b981', fontWeight: 700 }}>{l.cycleNo}</td>
                    <td style={{ fontWeight: 600, fontSize: 12 }}>{l.items}<div style={{ fontSize: 10, color: '#94a3b8' }}>→ {l.ward}</div></td>
                    <td style={{ fontSize: 12 }}>{l.method}</td>
                    <td style={{ fontSize: 12 }}>{l.startTime}–{l.endTime}</td>
                    <td>{l.temp}°C</td>
                    <td><span style={{ fontSize: 11, fontWeight: 700, color: l.bi === 'Pass' ? '#16a34a' : l.bi === 'Fail' ? '#dc2626' : '#f59e0b' }}>{l.bi === 'Pass' ? '✓ Pass' : l.bi === 'Fail' ? '✗ FAIL' : '⏳ Pending'}</span></td>
                    <td><span style={{ fontSize: 11, fontWeight: 700, color: l.ci === 'Pass' ? '#16a34a' : '#dc2626' }}>{l.ci === 'Pass' ? '✓ Pass' : '✗ Fail'}</span></td>
                    <td style={{ fontSize: 12 }}>{l.completedBy}</td>
                    <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: l.status === 'Released' ? '#f0fdf4' : '#fffbeb', color: l.status === 'Released' ? '#16a34a' : '#92400e', border: `1px solid ${l.status === 'Released' ? '#86efac' : '#fde68a'}` }}>{l.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function HandHygieneTab() {
  const { records: obs, create, loading } = useHmsRecords('infection-handhygiene');
  const [form, setForm] = useState({ ward: WARDS[0], opportunities: '', compliant: '', observedBy: '' });
  const [showForm, setShowForm] = useState(false);

  const totalOpp = obs.reduce((s, o) => s + Number(o.opportunities || 0), 0);
  const totalComp = obs.reduce((s, o) => s + Number(o.compliant || 0), 0);
  const overall = totalOpp > 0 ? Math.round((totalComp / totalOpp) * 100) : 0;

  const wardMap = {};
  obs.forEach(o => {
    if (!wardMap[o.ward]) wardMap[o.ward] = { ward: o.ward, opportunities: 0, compliant: 0 };
    wardMap[o.ward].opportunities += Number(o.opportunities || 0);
    wardMap[o.ward].compliant += Number(o.compliant || 0);
  });
  const wardRows = Object.values(wardMap);

  const submit = async () => {
    if (!form.opportunities || !form.compliant) return;
    await create({ ...form, opportunities: Number(form.opportunities), compliant: Number(form.compliant), date: new Date().toISOString().slice(0, 10) });
    setForm({ ward: WARDS[0], opportunities: '', compliant: '', observedBy: '' });
    setShowForm(false);
  };

  return (
    <div>
      {obs.length > 0 && (
        <div style={{ padding: '14px 18px', borderRadius: 12, background: overall >= 90 ? '#f0fdf4' : overall >= 75 ? '#fff7ed' : '#fef2f2', border: `2px solid ${overall >= 90 ? '#86efac' : overall >= 75 ? '#fed7aa' : '#fca5a5'}`, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 4 }}>Hospital-wide Hand Hygiene Compliance</div>
          <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 36, fontWeight: 900, color: overall >= 90 ? '#16a34a' : overall >= 75 ? '#d97706' : '#dc2626' }}>{overall}%</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{totalComp}/{totalOpp} opportunities observed · Target: ≥90%</div>
        </div>
      )}
      <div style={{ marginBottom: 12 }}><button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Log Audit Observation'}</button></div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #3b82f6' }}>
          <div className="field"><label>Ward</label><select className="input" value={form.ward} onChange={e => setForm(f => ({ ...f, ward: e.target.value }))}>{WARDS.map(w => <option key={w}>{w}</option>)}</select></div>
          <div className="field"><label>Opportunities Observed *</label><input type="number" className="input" value={form.opportunities} onChange={e => setForm(f => ({ ...f, opportunities: e.target.value }))} /></div>
          <div className="field"><label>Compliant Actions *</label><input type="number" className="input" value={form.compliant} onChange={e => setForm(f => ({ ...f, compliant: e.target.value }))} /></div>
          <div className="field"><label>Observed By</label><input className="input" value={form.observedBy} onChange={e => setForm(f => ({ ...f, observedBy: e.target.value }))} /></div>
          <div className="full form-actions"><button type="button" className="btn btn-primary" onClick={submit}>Submit Observation</button></div>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Ward-wise Hand Hygiene Audit</h3>
        {loading && <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>Loading...</div>}
        {!loading && obs.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>No hand hygiene audit observations recorded yet. Log the first observation above.</div>}
        {wardRows.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Ward</th><th>Opportunities</th><th>Compliant</th><th>Compliance %</th><th>Performance</th></tr></thead>
              <tbody>
                {wardRows.map(o => {
                  const pct = o.opportunities > 0 ? Math.round((o.compliant / o.opportunities) * 100) : 0;
                  const color = pct >= 90 ? '#10b981' : pct >= 75 ? '#f59e0b' : '#dc2626';
                  return (
                    <tr key={o.ward}>
                      <td style={{ fontWeight: 600 }}>{o.ward}</td>
                      <td>{o.opportunities}</td>
                      <td>{o.compliant}</td>
                      <td style={{ fontWeight: 800, color }}>{pct}%</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                          <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 10 }}>
                            <div style={{ width: `${pct}%`, background: color, height: 10, borderRadius: 4, transition: 'width 0.5s' }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 32 }}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function InfectionControlPage() {
  const [tab, setTab] = useState('hai');
  return (
    <PageShell
      title="Infection Control (HIC)"
      subtitle="HAI Surveillance · Antibiotic Stewardship · CSSD · Hand Hygiene — NABH Compliant"
      actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>🖨 Monthly HIC Report</button>}
    >
      <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
        🦠 NABH HIC Standard: All HAIs must be reported within 24 hours. Restricted antibiotics require ID approval. CSSD logs are mandatory NABH documentation.
      </div>
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'hai', label: '🦠 HAI Surveillance' }, { id: 'antibiotic', label: '💊 Antibiotic Stewardship' }, { id: 'cssd', label: '⚗️ CSSD / Sterilization' }, { id: 'handhygiene', label: '🤲 Hand Hygiene' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'hai' && <HaiSurveillanceTab />}
      {tab === 'antibiotic' && <AntibioticTab />}
      {tab === 'cssd' && <CssdTab />}
      {tab === 'handhygiene' && <HandHygieneTab />}
    </PageShell>
  );
}
